import * as React from "react";
import { StaticImageData } from "next/image";

import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import {
  CATALOG_HOME_LINK_TYPE,
  type LinkWithLangType,
} from "@stadium/shared/src/constants/stores/routes";

import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";

import { LinkWithLang } from "components/plasmic/shared/link-with-lang/client";
import styles from "./main.module.scss";

export interface ReviewBadge {
  logo: string;
  logoAlt?: string;
  rating: string;
  reviewCount: string;
  starIcon?: string;
}

export interface CompanyLogo {
  id: string;
  logo: string | StaticImageData;
  logoAlt: string;
  width?: number;
  height?: number;
}

export interface H07Props {
  // Hero Content
  heading?: string;
  description?: string;

  // CTA Buttons
  primaryButtonText?: string;
  primaryButtonLinkType?: string;
  primaryButtonOpenInNewTab?: boolean;
  primaryButtonOtherUrl?: string;
  primaryButtonCategorySlug?: string;
  primaryButtonWidth?: string;
  secondaryButtonText?: string;
  secondaryButtonLinkType?: string;
  secondaryButtonOpenInNewTab?: boolean;
  secondaryButtonOtherUrl?: string;
  secondaryButtonCategorySlug?: string;
  secondaryButtonWidth?: string;

  // Hero Image
  heroImage: string | StaticImageData;
  heroImageAlt?: string;
  heroImageBgColor?: string;

  // Review Badge
  showReviewBadge?: boolean;
  reviewBadgeLogo?: string | StaticImageData;
  reviewBadgeLogoAlt?: string;
  reviewRating?: string;
  reviewCount?: string;
  reviewStarIcon?: string;

  // Logo Strip Section
  showLogoStrip?: boolean;
  logoStripEyebrow?: string;
  logoStripHeading?: string;
  companyLogo1?: string | StaticImageData;
  companyLogo1Alt?: string;
  companyLogo2?: string | StaticImageData;
  companyLogo2Alt?: string;
  companyLogo3?: string | StaticImageData;
  companyLogo3Alt?: string;
  className?: string;
  linkRouteDefaults?: LinkRouteDefaults;
}

const H07: React.FC<Readonly<H07Props>> = ({
  heading = "Make Employee Recognition Easy and Impactful",
  description = "Celebrate your team with thoughtful swag—perfect for milestones, appreciation, or everyday wins.",
  primaryButtonText = "View Catalog",
  primaryButtonLinkType = CATALOG_HOME_LINK_TYPE,
  primaryButtonOpenInNewTab = false,
  primaryButtonOtherUrl,
  primaryButtonCategorySlug,
  primaryButtonWidth = "auto",
  secondaryButtonText = "Browse Kits",
  secondaryButtonLinkType = CATALOG_HOME_LINK_TYPE,
  secondaryButtonOpenInNewTab = false,
  secondaryButtonOtherUrl,
  secondaryButtonCategorySlug,
  secondaryButtonWidth = "auto",
  heroImage = "https://cdn.bystadium.com/plasmic/static-slices/b09-desktop-img.png",
  heroImageAlt = "Employee recognition",
  showReviewBadge = true,
  reviewBadgeLogo = "https://cdn.bystadium.com/plasmic/static-slices/h07-g2-logo.png",
  reviewBadgeLogoAlt = "G2 Reviews",
  reviewRating = "4.8",
  reviewCount = "1,515",
  reviewStarIcon = "https://cdn.bystadium.com/plasmic/static-slices/h07-star-icon.svg",
  showLogoStrip = true,
  logoStripEyebrow = "FOR EVERY POSSIBLE OCCASION",
  logoStripHeading = "Loved by teams everywhere",
  companyLogo1 = "https://cdn.bystadium.com/plasmic/static-slices/h07-agoda.png",
  companyLogo1Alt = "Agoda",
  companyLogo2 = "https://cdn.bystadium.com/plasmic/static-slices/h07-pinterest.png",
  companyLogo2Alt = "Pinterest",
  companyLogo3 = "https://cdn.bystadium.com/plasmic/static-slices/h07-amazon.png",
  companyLogo3Alt = "Amazon",
  className = "",
  linkRouteDefaults,
}) => {
  const companyLogos: CompanyLogo[] = [
    { id: "logo-1", logo: companyLogo1, logoAlt: companyLogo1Alt },
    { id: "logo-2", logo: companyLogo2, logoAlt: companyLogo2Alt },
    { id: "logo-3", logo: companyLogo3, logoAlt: companyLogo3Alt },
  ];
  return (
    <section className={`${styles.sectionWrapper} ${className}`}>
      {/* Main Hero Section */}
      <div className={styles.h07container}>
        <div className={styles.heroContent}>
          {/* Left Content */}
          <div className={styles.leftContent}>
            {/* Text Content */}
            <div className={styles.textContent}>
              <h1 className={styles.heading}>{heading}</h1>
              <p className={styles.description}>{description}</p>
            </div>

            {/* CTA Buttons */}
            <div className={styles.buttonWrapper}>
              {primaryButtonText && primaryButtonLinkType && (
                <LinkWithLang
                  {...linkRouteDefaults}
                  type={primaryButtonLinkType as LinkWithLangType}
                  data={{ code: primaryButtonCategorySlug, url: primaryButtonOtherUrl }}
                  target={primaryButtonOpenInNewTab ? "_blank" : "_self"}
                  showExternalLinkIcon={primaryButtonOpenInNewTab}
                  className={styles.primaryBtn}
                  style={{ width: primaryButtonWidth }}
                >
                  {primaryButtonText}
                </LinkWithLang>
              )}
              {secondaryButtonText && secondaryButtonLinkType && (
                <LinkWithLang
                  {...linkRouteDefaults}
                  type={secondaryButtonLinkType as LinkWithLangType}
                  data={{ code: secondaryButtonCategorySlug, url: secondaryButtonOtherUrl }}
                  target={secondaryButtonOpenInNewTab ? "_blank" : "_self"}
                  showExternalLinkIcon={secondaryButtonOpenInNewTab}
                  className={styles.primaryOutlineBtn}
                  style={{ width: secondaryButtonWidth }}
                >
                  {secondaryButtonText}
                </LinkWithLang>
              )}
            </div>

            {/* Review Badge */}
            {showReviewBadge && (
              <div className={styles.reviewBadge}>
                <div className={styles.badgeContent}>
                  {/* Logo and Star */}
                  <div className={styles.logoStarWrapper}>
                    <div className={styles.badgeLogo}>
                      <Multimedia
                        type="image"
                        src={reviewBadgeLogo}
                        alt={reviewBadgeLogoAlt}
                        className={styles.badgeLogoImage}
                      />
                    </div>
                    <div className={styles.starIcon}>
                      <Multimedia
                        type="image"
                        src={reviewStarIcon}
                        alt="Star-rating"
                        className={styles.starIconImage}
                      />
                    </div>
                  </div>

                  {/* Rating Info */}
                  <div className={styles.ratingInfo}>
                    <span className={styles.ratingValue}>{reviewRating}</span>
                    <span className={styles.divider} />
                    <span className={styles.ratingValue}>{reviewCount}</span>
                    <span className={styles.ratingText}>Reviews</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Image */}
          <div className={styles.rightImage}>
            <Multimedia
              type="image"
              src={heroImage}
              alt={heroImageAlt}
              className={styles.h07Image}
            />
          </div>
        </div>
      </div>

      {/* Logo Strip Section */}
      {showLogoStrip && (
        <div className={styles.h07container}>
          <div className={styles.logoStripSection}>
            {/* Left Text */}
            <div className={styles.logoStripLeft}>
              <p className={styles.eyebrow}>{logoStripEyebrow}</p>
              <h2 className={styles.logoStripHeading}>{logoStripHeading}</h2>
            </div>

            {/* Right Logos */}
            <div className={styles.logoStripRight}>
              {companyLogos.map((logo) => (
                <div key={logo.id} className={styles.logoBox}>
                  <Multimedia
                    type="image"
                    src={logo.logo}
                    alt={logo.logoAlt}
                    className={styles.logoImages}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
export default H07;
